import { useCallback, useEffect, useState } from "react";
import { Loader2, Plane, HardHat, Trophy, Bell, X, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePartner } from "../PartnerContext";
import { Card, PageHeader, Pill, Placeholder } from "../ui";
import { euro } from "@/data/partnerConfig";

const STATUS_LABELS = { a_facturer: "À facturer", facturee: "Facturée", a_payer: "À payer", payee: "Payée", encaissee: "Encaissée", annulee: "Annulée", avoir: "Avoir" };
const STATUS_TONE = { a_facturer: "stone", facturee: "petrol", a_payer: "amber", payee: "petrol", encaissee: "sage", annulee: "red", avoir: "red" };

const ProgressBar = ({ value, max }) => (
  <div className="h-3 w-full overflow-hidden rounded-full bg-petrol-bg">
    <div className="h-full rounded-full bg-gradient-to-r from-cognac to-cognac-light transition-all duration-500" style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
  </div>
);

export default function PartnerCommissions() {
  const [year, setYear] = useState(2026);
  const [summary, setSummary] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/phase3/partner/commissions/summary", { params: { year } }),
      api.get("/phase3/partner/commissions", { params: { year } }),
      api.get("/phase3/partner/commissions/notifications"),
      api.get("/phase3/partner/commissions/exports"),
    ]).then(([s, c, n, e]) => { setSummary(s.data); setCommissions(c.data); setNotifications(n.data); setExports(e.data); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const dismissNotification = async (id) => {
    try {
      await api.patch(`/phase3/partner/commissions/notifications/${id}/read`);
      setNotifications((list) => list.filter((n) => n.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unread = notifications.filter((n) => !n.read);
  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/phase3/partner/commissions/export.pdf", { params: { year }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `cozyhome-commissions-${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setExports((list) => [{ id: `local-${Date.now()}`, year, filename: `cozyhome-commissions-${year}.pdf`, exported_by: "vous", exported_at: new Date().toISOString() }, ...list]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'export PDF.");
    } finally { setExporting(false); }
  };

  return (
    <div data-testid="partner-commissions">
      <PageHeader
        eyebrow="Réseau CozyHome"
        title="Commissions & Avantages"
        sub="Suivez vos marchés apportés au réseau, la part Centrale générée et les avantages que vous débloquez."
        actions={
          <div className="flex items-center gap-2">
            <select data-testid="partner-commissions-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="rounded-full border border-[#D9E0E2] bg-white px-4 py-2 text-xs font-bold text-navy focus:border-petrol focus:outline-none">
              {[2026, 2027, 2025].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              data-testid="partner-commissions-export-pdf" onClick={exportPdf} disabled={exporting}
              className="inline-flex items-center gap-1.5 rounded-full border border-petrol bg-white px-4 py-2 text-xs font-bold text-petrol transition-colors hover:bg-petrol-bg disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export comptable (PDF)
            </button>
          </div>
        }
      />

      {unread.length > 0 && (
        <div className="mb-5 space-y-2">
          {unread.map((n) => (
            <div key={n.id} data-testid={`commission-notification-${n.id}`} className="flex items-start gap-3 rounded-2xl bg-cognac/10 p-4 text-sm text-cognac-dark">
              <Bell className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="flex-1 leading-relaxed">{n.message}</p>
              <button data-testid={`commission-notification-dismiss-${n.id}`} onClick={() => dismissNotification(n.id)} aria-label="Marquer comme lu" className="rounded-full p-1 text-cognac-dark/60 hover:text-cognac-dark">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {loading || !summary ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-petrol" /></div>
      ) : (
        <>
          <Card testid="commissions-centrale-progress" className="p-6">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">Commissions Centrale générées en {year}</p>
            <p className="mt-2 font-display text-3xl font-medium text-navy sm:text-4xl" data-testid="commissions-centrale-total">
              {euro(summary.progress.centrale_total)} {summary.progress.next_threshold ? `/ ${euro(summary.progress.next_threshold)}` : ""}
            </p>
            {summary.progress.next_threshold && (
              <>
                <div className="mt-3"><ProgressBar value={summary.progress.centrale_total} max={summary.progress.next_threshold} /></div>
                <p className="mt-2 text-sm text-stone-500">
                  Prochain palier : <span className="font-semibold text-navy">{euro(summary.progress.next_threshold)}</span> —
                  Reste à générer : <span className="font-semibold text-navy">{euro(summary.progress.remaining_to_next_threshold)}</span>
                </p>
              </>
            )}
          </Card>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Card testid="commissions-material" className="p-5">
              <HardHat className="h-4 w-4 text-cognac" />
              <p className="mt-2 text-xs font-bold uppercase tracking-wider text-stone-400">Avantage matériel</p>
              <p className="mt-1 font-display text-xl font-medium text-navy">{euro(summary.progress.material.available)} disponible</p>
              <p className="mt-1 text-xs text-stone-500">Budget {euro(summary.progress.material.budget_total)} — consommé {euro(summary.progress.material.consumed)}</p>
            </Card>
            <Card testid="commissions-travel" className="p-5">
              <Plane className="h-4 w-4 text-cognac" />
              <p className="mt-2 text-xs font-bold uppercase tracking-wider text-stone-400">Voyage CozyHome</p>
              <p className="mt-1 font-display text-xl font-medium text-navy">
                {summary.progress.travel.qualified ? `${summary.progress.travel.label} — ${summary.progress.travel.partner_pct}% à charge` : "Non qualifié"}
              </p>
              {summary.progress.travel.next_threshold && (
                <p className="mt-1 text-xs text-stone-500">
                  À {euro(summary.progress.travel.next_threshold)}, participation à {summary.progress.travel.next_partner_pct}%
                </p>
              )}
            </Card>
            <Card testid="commissions-bonus" className="p-5">
              <Trophy className="h-4 w-4 text-cognac" />
              <p className="mt-2 text-xs font-bold uppercase tracking-wider text-stone-400">Prime Excellence</p>
              <p className="mt-1 font-display text-xl font-medium text-navy">
                {summary.progress.bonus.unlocked ? `${euro(summary.progress.bonus.amount)} débloquée` : `Débloquée à ${euro(summary.progress.bonus.threshold)}`}
              </p>
            </Card>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              ["Marchés HT encaissés", euro(summary.total_marches_ht)],
              ["Commission totale", euro(summary.total_commission)],
              ["Part partenaire versée", euro(summary.total_partner_amount)],
              ["Part Centrale impayée", euro(summary.total_centrale_impaye)],
            ].map(([label, value]) => (
              <Card key={label} className="p-4">
                <p className="font-display text-lg font-semibold text-navy">{value}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider text-stone-400">{label}</p>
              </Card>
            ))}
          </div>

          <Card testid="commissions-list" className="mt-6 overflow-hidden">
            <p className="border-b border-[#EDF0F1] px-5 py-4 text-xs font-bold uppercase tracking-wider text-stone-400">Marchés apportés au réseau en {year}</p>
            {commissions.length === 0 ? (
              <Placeholder testid="commissions-empty" className="m-5">Aucun marché pour cette année.</Placeholder>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-[#EDF0F1] text-xs uppercase tracking-wider text-stone-400">
                      <th className="px-5 py-3">Entreprise</th>
                      <th className="px-5 py-3">Projet</th>
                      <th className="px-5 py-3 text-right">Montant HT</th>
                      <th className="px-5 py-3 text-right">Commission (5%)</th>
                      <th className="px-5 py-3 text-right">Ma part (75%)</th>
                      <th className="px-5 py-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((c) => (
                      <tr key={c.id} data-testid={`commission-row-${c.id}`} className="border-b border-[#F0F2F3] last:border-0">
                        <td className="px-5 py-3 font-semibold text-navy">{c.company_name}</td>
                        <td className="px-5 py-3 text-stone-500">{c.project_reference}</td>
                        <td className="px-5 py-3 text-right text-stone-600">{euro(c.amount_ht)}</td>
                        <td className="px-5 py-3 text-right text-stone-600">{euro(c.commission_total)}</td>
                        <td className="px-5 py-3 text-right font-semibold text-navy">{euro(c.partner_amount)}</td>
                        <td className="px-5 py-3"><Pill tone={STATUS_TONE[c.status]}>{STATUS_LABELS[c.status]}</Pill></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card testid="commissions-export-history" className="mt-6 overflow-hidden">
            <p className="border-b border-[#EDF0F1] px-5 py-4 text-xs font-bold uppercase tracking-wider text-stone-400">Historique des exports comptables</p>
            {exports.length === 0 ? (
              <Placeholder testid="commissions-export-history-empty" className="m-5">Aucun export téléchargé pour l'instant.</Placeholder>
            ) : (
              <ul className="divide-y divide-[#F0F2F3]">
                {exports.map((e) => (
                  <li key={e.id} data-testid={`commission-export-${e.id}`} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                    <div className="flex items-center gap-2 text-stone-500">
                      <Download className="h-3.5 w-3.5 text-cognac" />
                      <span className="font-semibold text-navy">{e.filename}</span>
                      <span>· {e.year}</span>
                    </div>
                    <span className="text-xs text-stone-400">{new Date(e.exported_at).toLocaleString("fr-FR")} — {e.exported_by}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
